import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { AuthContext } from "../../context/AuthContext"; // sesuaikan path
import PimpinanLayout from "../../components/layout/PimpinanLayout";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import ChangePasswordModal from "../../components/modal/Password";

const API = "https://backend.smpn1kamal.sch.id/api";
const BASE_URL = "https://backend.smpn1kamal.sch.id";
const DEFAULT_AVATAR = "/iconpengguna.avif";

export default function ProfilPimpinan() {
  const { token } = useContext(AuthContext); // ← pakai session token

  const [profil, setProfil] = useState({
    username: "",
    email: "",
    nama_lengkap: "",
    nip: "",
    jabatan: "",
    foto_profil: "",
  });

  const [fotoPreview, setFotoPreview] = useState(DEFAULT_AVATAR);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (token) fetchProfil();
  }, [token]);

  /* ==============================
     GET PROFIL
  ============================== */
  const fetchProfil = async () => {
    try {
      const res = await axios.get(`${API}/pimpinan/profil`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = res.data.data;

      setProfil({
        username: d.username || "",
        email: d.email || "",
        nama_lengkap: d.nama_lengkap || "",
        nip: d.nip || "",
        jabatan: d.jabatan || "",
        foto_profil: d.foto_profil || "",
      });

      if (d.foto_profil) {
        setFotoPreview(`${BASE_URL}/uploads/foto-profil/${d.foto_profil}`);
      } else {
        setFotoPreview(DEFAULT_AVATAR);
      }
    } catch {
      toast.error("Gagal memuat profil");
    }
  };

  /* ==============================
     UPDATE PROFIL (POST)
  ============================== */
  const handleUpdateProfil = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/pimpinan/profil`,
        {
          nama_lengkap: profil.nama_lengkap,
          nip: profil.nip,
          jabatan: profil.jabatan,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Profil berhasil diperbarui");
      setShowEditModal(false);
      fetchProfil();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal update profil");
    }
  };

  /* ==============================
     UPLOAD FOTO
  ============================== */
  const handleUploadFoto = async (file) => {
    const fd = new FormData();
    fd.append("foto_profil", file);

    try {
      await axios.post(`${API}/upload`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Foto berhasil diubah");
      fetchProfil();
    } catch {
      toast.error("Gagal upload foto");
    }
  };

  /* ==============================
     DELETE FOTO
  ============================== */
  const handleDeleteFoto = async () => {
    try {
      await axios.delete(`${API}/hapus-foto`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFotoPreview(DEFAULT_AVATAR);
      toast.success("Foto dihapus");
      fetchProfil();
    } catch {
      toast.error("Gagal hapus foto");
    }
  };

  return (
    <PimpinanLayout>
      <div className="max-w-6xl mx-auto space-y-5">

        {/* HEADER */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-blue-600">Profil Pimpinan</h1>
          <p className="text-sm text-slate-500">Informasi akun dan data pimpinan</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* SIDEBAR */}
          <div className="space-y-5">

            {/* FOTO */}
            <div className="bg-white rounded-2xl shadow-sm p-5 text-center">
              <div className="relative inline-block">
                <div className="w-28 h-28 rounded-full overflow-hidden border bg-slate-100 mx-auto">
                  <img
                    src={fotoPreview}
                    onError={() => setFotoPreview(DEFAULT_AVATAR)}
                    className="w-full h-full object-cover"
                    alt="Foto Profil"
                  />
                </div>

                <label className="absolute bottom-1 right-1 bg-blue-600 p-2 rounded-full cursor-pointer">
                  <Camera size={14} color="white" />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFotoPreview(URL.createObjectURL(file));
                        handleUploadFoto(file);
                      }
                    }}
                  />
                </label>
              </div>

              <button
                onClick={handleDeleteFoto}
                className="mt-3 mx-auto flex items-center gap-2 text-red-500 text-xs px-3 py-1.5 rounded-full border border-red-200 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Hapus Foto
              </button>

              <p className="text-xs text-slate-500 mt-2">
                Klik icon untuk ganti foto
              </p>
            </div>

            {/* PASSWORD */}
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <p className="text-xs text-slate-500 mb-1">Kata Sandi</p>
              <p className="tracking-widest mb-4">***********</p>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-full text-sm"
              >
                Perbarui kata sandi
              </button>
            </div>
          </div>

          {/* DATA DIRI */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-base font-semibold mb-4">Data Diri</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <View label="Username" value={profil.username} />
              <View label="Email" value={profil.email} />
              <View label="Nama Lengkap" value={profil.nama_lengkap} />
              <View label="NIP" value={profil.nip} />
              <View label="Jabatan" value={profil.jabatan} />
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowEditModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm"
              >
                Edit Profil
              </button>
            </div>
          </div>
        </div>

        {/* MODAL EDIT */}
        {showEditModal &&
          createPortal(
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[99999]">
              <div className="bg-white w-full max-w-lg p-6 rounded-2xl">
                <h2 className="text-lg font-semibold mb-4">Edit Profil</h2>

                <form onSubmit={handleUpdateProfil} className="space-y-4">
                  <Input label="Username" value={profil.username} readOnly />
                  <Input label="Email" value={profil.email} readOnly />
                  <Input
                    label="Nama Lengkap"
                    value={profil.nama_lengkap}
                    onChange={(v) => setProfil({ ...profil, nama_lengkap: v })}
                  />
                  <Input
                    label="NIP"
                    value={profil.nip}
                    onChange={(v) => setProfil({ ...profil, nip: v })}
                  />

                  {/* DROPDOWN JABATAN */}
                  <div>
                    <label className="text-xs text-slate-500">Jabatan</label>
                    <select
                      value={profil.jabatan}
                      onChange={(e) =>
                        setProfil({ ...profil, jabatan: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">Pilih Jabatan</option>
                      <option value="kepala sekolah">Kepala Sekolah</option>
                      <option value="wakil kepala sekolah">Wakil Kepala Sekolah</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-full text-sm"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 bg-blue-600 text-white rounded-full text-sm"
                    >
                      Simpan
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

        {showPasswordModal && (
          <ChangePasswordModal
            token={token}
            onClose={() => setShowPasswordModal(false)}
          />
        )}
      </div>
    </PimpinanLayout>
  );
}

/* VIEW */
function View({ label, value }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <div className="px-3 py-2 border rounded-lg bg-slate-50 text-sm">
        {value || "-"}
      </div>
    </div>
  );
}

/* INPUT */
function Input({ label, value, onChange, readOnly = false }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input
        value={value}
        readOnly={readOnly}
        onChange={(e) => !readOnly && onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-lg text-sm"
      />
    </div>
  );
}